import React, { useMemo, useState } from "react";
import moment from "moment";
import { WIDGET_NAMES } from "../../Dashboard.consts";
import { WidgetBox } from "../../boxes/WidgetBox/WidgetBox.js";
import { useRevenuePerHour } from "./hooks/useRevenuePerHour.js";
import RevenuePerHourChart from "./components/RevenuePerHourChart.js";
import { API_DATE_FORMAT } from "../../../../consts/api.js";
import { slicePaginationData } from "../../Dashboard.utils.js";
import { Select } from "../../../../shared/Select/Select.js";
import styles from "../../sass/WidgetRevenuePerHour.module.scss";

const ITEMS_PER_PAGE = 4;
const PAGINATION_RANGE = 5;

const revenuePerHourOptions = [
  {
    label: "Clocked-In Hours",
    value: "time_card",
  },
  {
    label: "Scheduled Hours",
    value: "schedule",
  },
];

const WidgetRevenuePerHour = ({ fromReportData }) => {
  const [period, setPeriod] = useState({
    from: moment().startOf("month").toDate(),
    to: moment().endOf("month").toDate(),
  });

  const [type, setType] = useState({
    label: "Clocked-In Hours",
    value: "time_card",
  });

  const [activePage, setActivePage] = useState(1);

  const params = fromReportData || {
    from_date: moment(period.from).format(API_DATE_FORMAT),
    to_date: moment(period.to).format(API_DATE_FORMAT),
    type: type.value,
  };

  const { data: widgetData, isLoading: isWidgetDataLoading } =
    useRevenuePerHour(params, {
      select: (data) => data.data.data.data || [],
      enabled: Boolean(params.from_date) && !fromReportData,
    });

  const monthlyRange = !fromReportData
    ? {
        dates: {
          start: period.from,
          end: period.to,
        },
        update: setPeriod,
        isNextDisabled: moment(period.to).isSameOrAfter(moment(), "month"),
      }
    : null;

  const slicedReportData = useMemo(
    () =>
      slicePaginationData({
        activePage,
        itemsPerPage: ITEMS_PER_PAGE,
        data: fromReportData?.data || widgetData,
      }),
    [widgetData, activePage, fromReportData],
  );

  const providerNames = useMemo(
    () => slicedReportData?.map((r) => r.provider_name),
    [slicedReportData],
  );

  const netRevenue = useMemo(
    () => slicedReportData?.map((r) => r.net_revenue_hour_raw),
    [slicedReportData],
  );

  const grossRevenue = useMemo(
    () => slicedReportData?.map((r) => r.gross_revenue_hour_raw),
    [slicedReportData],
  );

  const formattedNetRevenue = useMemo(
    () => slicedReportData?.map((r) => r.net_revenue_hour),
    [slicedReportData],
  );

  const formattedGrossRevenue = useMemo(
    () => slicedReportData?.map((r) => r.gross_revenue_hour),
    [slicedReportData],
  );

  const hoursWorked = useMemo(
    () => slicedReportData.map((r) => r.hours_worked_period),
    [slicedReportData],
  );

  const chartData = {
    providerName: providerNames || [],
    netRevenue: netRevenue || [],
    grossRevenue: grossRevenue || [],
    formattedNetRevenue: formattedNetRevenue || [],
    formattedGrossRevenue: formattedGrossRevenue || [],
    hoursWorked: hoursWorked || [],
  };

  const showSelectInWidget = !fromReportData;

  return (
    <WidgetBox
      label={WIDGET_NAMES.revenue_per_hour}
      isLoading={!fromReportData ? isWidgetDataLoading : false}
      isEmpty={widgetData?.length === 0}
      headerClassName="flex justify-between items-center"
      monthlyRange={monthlyRange}
      pagination={{
        activePage,
        itemsCountPerPage: ITEMS_PER_PAGE,
        total: fromReportData?.data?.length || widgetData?.length,
        range: PAGINATION_RANGE,
        setActivePage,
      }}
      footerFilters={
        showSelectInWidget && (
          <div className={styles.footerSelectContainer}>
            <Select
              size="small"
              options={revenuePerHourOptions}
              value={type}
              onChange={(option) => setType(option)}
            />
          </div>
        )
      }
    >
      <RevenuePerHourChart chartData={chartData} />
    </WidgetBox>
  );
};

export default WidgetRevenuePerHour;
